import {
  truncateWithEllipsis,
  getHighlightedTitleHtml,
} from "../utils/searchBarResultHelper";

/**
 * Class representing search bar suggestions.
 */
export default class SearchBarSuggestions {
  constructor(container, suggestions = [], searchText = '', isMobile = false) {
    this.container = container;
    this.suggestions = Array.isArray(suggestions) ? suggestions : [];
    this.searchText = searchText.trim();
    this.isMobile = Boolean(isMobile);

    this.TEXT_LIMITS = {
      MOBILE: 45,
      DESKTOP: 83,
    };
  }

  /**
   * Gets the template for a search suggestion item.
   * @returns {string} The HTML template for a search suggestion item.
   */
  static get template() {
    return `
      <div class="nhs-n1-c-search-bar-results__item" data-search-bar-result-item {{ATTRIBUTES}}>
        <div class="nhs-n1-c-search-bar-results__item__container">
          <div>{{TITLE}}</div>
          <div class="nhs-n1-c-search-bar-results__item__container__description">
            {{DESCRIPTION}}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Creates a string of HTML attributes from the given data object.
   * @param {Object} data - The data object containing attributes.
   * @returns {string} A string of HTML attributes.
   */
  static createAttributes(data = {}) {
    const attributes = {
      'data-location-url': data.LocationUrl || '#',
      'data-city': data.CityName,
      'data-name': data?.Name,
      'data-geo-location-state': data.StateEquivalentAbbreviation,
      'data-completion-suggestion-type': data?.CompletionSuggestionType,
      'data-market-id': data.MarketAreaId,
      'data-market-name': data.MarketAreaName,
      'data-market-state': data.MarketPreferredStateEquivalentAbbreviation,
      'data-market-state-name': data.MarketPreferredStateEquivalentName,
      'data-origin-latitude': data.Latitude,
      'data-origin-longitude': data.Longitude,
      'data-value': data.DisplayText,
      'data-neighborhood-name': data?.NeighborhoodName,
      'data-zip': data?.PostalCode,
      'data-completion-suggestion-id': data?.CompletionSuggestionId,
      'data-category-type': data?.SearchBarResultCategoryType,
    };

    // Filter out falsy attributes to avoid empty data- attributes
    return Object.entries(attributes)
      .filter(([, value]) => value)
      .map(([key, value]) => `${key}="${String(value).replace(/"/g, '&quot;')}"`)
      .join(' ');
  }

  /**
   * Builds the HTML for the search results.
   * @returns {string} The HTML string for the search results.
   */
  buildResultsHtml() {
    if (!this.suggestions.length) {return '';}

    const textLimit = this.isMobile
      ? this.TEXT_LIMITS.MOBILE
      : this.TEXT_LIMITS.DESKTOP;

    return this.suggestions
      .map((item) => {
        const truncated = truncateWithEllipsis(item.DisplayText, textLimit);
        const highlightedTitle = getHighlightedTitleHtml(truncated, this.searchText);
        const attributes = SearchBarSuggestions.createAttributes(item);
        const description = item.DisplayDescription || '';

        return SearchBarSuggestions.template
          .replace('{{ATTRIBUTES}}', attributes)
          .replace('{{TITLE}}', highlightedTitle)
          .replace('{{DESCRIPTION}}', description);
      })
      .join('');
  }

  /**
   * Renders the search suggestions into the container.
   */
  render() {
    if (!this.container) {return;}

    const resultsList = this.container.querySelector('[data-search-results-list]');
    if (!resultsList) {return;}

    resultsList.innerHTML = this.buildResultsHtml();
  }
}
